import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search as SearchIcon, Loader2, ExternalLink, Copy, Check, X, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, FolderPlus, Filter, Users, Building2, Calendar } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { getCollectionsQuery } from '@/graphql/queries/getCollections';
import { createAssetDirectDB } from '@/graphql/mutations/createAsset';
import { SearchImageViewer, SearchImageViewerState } from '@/components/search/SearchImageViewer';
import { supabase } from '@/integrations/supabase/client';
import { extractMarkerFromUrl } from '@/utils/cloudinaryUtils';
import { cleanHighlightWithLLM } from '@/utils/llmUtils';

const SUPABASE_URL = 'https://qulnbyjrczvoxemtrili.supabase.co';

// Filter options
const CATEGORY_OPTIONS = [
  'AI & Machine Learning',
  'Business Operations',
  'Cloud & DevOps',
  'Collaboration & Productivity',
  'Cybersecurity & Identity',
  'Data & Analytics',
  'Developer Tools',
  'Finance & Accounting',
  'Marketing & Content',
  'Project Management',
  'Sales & CRM',
  'Software Development & Testing',
  'Workforce Management',
];

const EMPLOYEE_OPTIONS = [
  '1-10',
  '11-50',
  '51-100',
  '101-1000',
  '1001-10000',
  '10000+',
];

const PERIOD_OPTIONS = [
  '2025-Q4',
  '2025-Q3',
  '2025-Q2',
  '2025-Q1',
  '2024-Q4',
  '2024-Q3',
  '2024-Q2',
  '2024-Q1',
  '2023-Q4',
  '2023-Q3',
  '2023-Q2',
  '2023-Q1',
];

interface PeriodInfo {
  quarter?: string;
  month?: string;
  week?: string;
}

interface SearchResultItem {
  slug: string;
  company_name: string;
  version: string;
  period?: string | PeriodInfo;  // Optional - not all search results include period
  matched_phrase?: string;
  original_query?: string;
  text_preview?: string;
  score?: number;  // 0-100 relevance score
  confidence?: number;  // Legacy field
  // Image URLs from updated API
  image_url?: string;        // Full original image
  cropped_url?: string;      // Pre-cropped around match
  annotated_url?: string;    // Pre-annotated with marker
  // Legacy field names (for backwards compatibility)
  cloudinary_url?: string;
  viewer_url?: string;
  // Additional fields
  gemini_rank?: number;
  matched_lines?: string[];
  category_name?: string;
  employees?: string;
}

// Helper to format period for display
function formatPeriod(period: string | PeriodInfo | undefined): string {
  if (!period) return '';
  if (typeof period === 'string') return period;
  // Prefer quarter, then month, then week
  return period.quarter || period.month || period.week || '';
}

// Helper to capitalize words (black friday => Black Friday)
function capitalizeWords(text: string): string {
  return text
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// Helper to highlight query terms in text
function highlightQueryTerms(text: string, query: string, darkMode = false): React.ReactNode {
  if (!text || !query) return text;

  // Split query into words (ignore very short words)
  const queryWords = query.toLowerCase().split(/\s+/).filter(w => w.length > 2);
  if (queryWords.length === 0) return text;

  // Create regex pattern for all query words
  const pattern = new RegExp(`(${queryWords.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');

  // Split text by matches
  const parts = text.split(pattern);

  // Use different styling for dark vs light backgrounds
  const highlightClass = darkMode
    ? "text-white font-bold"
    : "text-gray-900 font-semibold";

  return parts.map((part, i) => {
    const isMatch = queryWords.some(w => part.toLowerCase() === w);
    if (isMatch) {
      return <strong key={i} className={highlightClass}>{part}</strong>;
    }
    return part;
  });
}


interface EnhancedSearchResponse {
  success: boolean;
  query: string;
  expanded_phrases: string[];
  total_results: number;
  companies_with_matches: number;
  companies_searched: number;
  results: SearchResultItem[];
  metadata: {
    search_time_ms: number;
    semantic_results: number;
    phrase_expansions_used: boolean;
    periods_searched: string;
  };
}

export const Search: React.FC = () => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<EnhancedSearchResponse | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Advanced filter state
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Multi-select state
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  // Collection modal state
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [itemsToAdd, setItemsToAdd] = useState<SearchResultItem[]>([]);

  // Score filter state
  const [showAllResults, setShowAllResults] = useState(false);

  const { toast } = useToast();

  // Toggle item selection
  const toggleSelection = (index: number) => {
    setSelectedIndices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      // Auto-disable selection mode when no items selected
      if (newSet.size === 0) {
        setIsSelectionMode(false);
      } else if (!isSelectionMode) {
        setIsSelectionMode(true);
      }
      return newSet;
    });
  };

  // Clear all selections
  const clearSelection = () => {
    setSelectedIndices(new Set());
    setIsSelectionMode(false);
  };

  // Open collection modal for selected items
  const handleAddSelectedToCollection = () => {
    if (response && selectedIndices.size > 0) {
      const items = Array.from(selectedIndices).map(i => response.results[i]);
      setItemsToAdd(items);
      setShowCollectionModal(true);
    }
  };

  // Track viewer state for single item add
  const [singleItemViewerState, setSingleItemViewerState] = useState<SearchImageViewerState | null>(null);

  // Persist viewer states across lightbox navigation (keyed by slug-version)
  const [viewerStatesMap, setViewerStatesMap] = useState<Map<string, SearchImageViewerState>>(new Map());

  // Get key for a result item
  const getResultKey = (item: SearchResultItem) => `${item.slug}-${item.version}`;

  // Update viewer state for a specific result
  const updateViewerState = useCallback((item: SearchResultItem, state: SearchImageViewerState) => {
    setViewerStatesMap(prev => {
      const newMap = new Map(prev);
      newMap.set(getResultKey(item), state);
      return newMap;
    });
  }, []);

  // Get saved viewer state for a result
  const getSavedViewerState = useCallback((item: SearchResultItem): SearchImageViewerState | undefined => {
    return viewerStatesMap.get(getResultKey(item));
  }, [viewerStatesMap]);

  // Open collection modal for single item (from lightbox)
  const handleAddSingleToCollection = (item: SearchResultItem, viewerState?: SearchImageViewerState) => {
    setItemsToAdd([item]);
    // Use provided viewerState or fall back to saved state
    setSingleItemViewerState(viewerState || getSavedViewerState(item) || null);
    setShowCollectionModal(true);
  };

  const handleSearch = async () => {
    if (!query.trim()) {
      setError('Please enter a search query');
      return;
    }

    setLoading(true);
    setError(null);
    setResponse(null);
    clearSelection(); // Clear selections on new search
    setShowAllResults(false); // Reset score filter on new search

    // V3: Fast JSON search

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token;

      const res = await fetch(`${SUPABASE_URL}/functions/v1/ocr-search-enhanced`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
        },
        body: JSON.stringify({
          query: query.trim(),
          limit: 50,
          unique_companies: true, // Deduplicate results by company
          // version: 3 is default, no need to specify
          // Advanced filters
          ...(selectedCategories.length > 0 && { categories: selectedCategories }),
          ...(selectedEmployees.length > 0 && { employees: selectedEmployees }),
          ...(selectedPeriods.length > 0 && { periods: selectedPeriods }),
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `Search failed with status ${res.status}`);
      }

      const data = await res.json();

      // Build response in expected format
      const searchResponse: EnhancedSearchResponse = {
        success: data.success,
        query: data.query,
        expanded_phrases: [],
        total_results: data.total_results || 0,
        companies_with_matches: 0,
        companies_searched: 0,
        results: data.results || [],
        metadata: {
          search_time_ms: data.metadata?.search_time_ms || 0,
          semantic_results: data.metadata?.api_results || 0,
          phrase_expansions_used: false,
          periods_searched: '',
        },
      };

      setResponse(searchResponse);

      if (searchResponse.total_results === 0) {
        setError(`No matches found for "${query.trim()}"`);
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        setError('Search timed out. Try a more specific query.');
      } else {
        setError(err instanceof Error ? err.message : 'Search failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleSearch();
    }
  };

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
  };

  const goToPrev = useCallback(() => {
    if (lightboxIndex !== null && response) {
      setLightboxIndex(lightboxIndex > 0 ? lightboxIndex - 1 : response.results.length - 1);
    }
  }, [lightboxIndex, response]);

  const goToNext = useCallback(() => {
    if (lightboxIndex !== null && response) {
      setLightboxIndex(lightboxIndex < response.results.length - 1 ? lightboxIndex + 1 : 0);
    }
  }, [lightboxIndex, response]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;

      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [lightboxIndex, goToPrev, goToNext]);

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Search</h1>
        <p className="text-gray-500">
          Search for text across all pricing page screenshots
        </p>
      </div>

      {/* Search input */}
      <div className="mb-6 space-y-3">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search for text (e.g., 'Enterprise', 'free trial', 'limited time sale')"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              className="pl-10"
            />
          </div>
          <Button onClick={handleSearch} disabled={loading || !query.trim()}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Searching...
              </>
            ) : (
              'Search'
            )}
          </Button>
        </div>

        {/* Advanced Filters Toggle */}
        {/* <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          disabled={loading}
          className="flex items-center gap-2"
        >
          <Filter className="h-4 w-4" />
          Advanced Filters
          {(selectedCategories.length + selectedEmployees.length + selectedPeriods.length) > 0 && (
            <Badge variant="secondary" className="ml-1">
              {selectedCategories.length + selectedEmployees.length + selectedPeriods.length}
            </Badge>
          )}
        </Button> */}
      </div>

      {/* Advanced Filters Panel */}
      {/* {showAdvancedFilters && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Categories Filter */}
            {/* <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Building2 className="h-4 w-4" />
                Categories
              </label>
              <Select
                value=""
                onValueChange={(value) => {
                  if (!selectedCategories.includes(value)) {
                    setSelectedCategories([...selectedCategories, value]);
                  }
                }}
                disabled={loading}
              >
                <SelectTrigger className="bg-white" disabled={loading}>
                  <SelectValue placeholder="Select categories..." />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex flex-wrap gap-1">
                {selectedCategories.map(cat => (
                  <Badge key={cat} variant="secondary" className="text-xs">
                    {cat}
                    {!loading && (
                      <X
                        className="h-3 w-3 ml-1 cursor-pointer"
                        onClick={() => setSelectedCategories(selectedCategories.filter(c => c !== cat))}
                      />
                    )}
                  </Badge>
                ))}
              </div>
            </div> */}

            {/* Employees Filter */}
            {/* <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Users className="h-4 w-4" />
                Company Size
              </label>
              <Select
                value=""
                onValueChange={(value) => {
                  if (!selectedEmployees.includes(value)) {
                    setSelectedEmployees([...selectedEmployees, value]);
                  }
                }}
                disabled={loading}
              >
                <SelectTrigger className="bg-white" disabled={loading}>
                  <SelectValue placeholder="Select sizes..." />
                </SelectTrigger>
                <SelectContent>
                  {EMPLOYEE_OPTIONS.map(emp => (
                    <SelectItem key={emp} value={emp}>{emp} employees</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex flex-wrap gap-1">
                {selectedEmployees.map(emp => (
                  <Badge key={emp} variant="secondary" className="text-xs">
                    {emp}
                    {!loading && (
                      <X
                        className="h-3 w-3 ml-1 cursor-pointer"
                        onClick={() => setSelectedEmployees(selectedEmployees.filter(e => e !== emp))}
                      />
                    )}
                  </Badge>
                ))}
              </div>
            </div> */}

            {/* Periods Filter */}
            {/* <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Calendar className="h-4 w-4" />
                Periods
              </label>
              <Select
                value=""
                onValueChange={(value) => {
                  if (!selectedPeriods.includes(value)) {
                    setSelectedPeriods([...selectedPeriods, value]);
                  }
                }}
                disabled={loading}
              >
                <SelectTrigger className="bg-white" disabled={loading}>
                  <SelectValue placeholder="Select periods..." />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map(period => (
                    <SelectItem key={period} value={period}>{period}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex flex-wrap gap-1">
                {selectedPeriods.map(period => (
                  <Badge key={period} variant="secondary" className="text-xs">
                    {period}
                    {!loading && (
                      <X
                        className="h-3 w-3 ml-1 cursor-pointer"
                        onClick={() => setSelectedPeriods(selectedPeriods.filter(p => p !== period))}
                      />
                    )}
                  </Badge>
                ))}
              </div>
            </div> */}
          {/* </div> */}

          {/* Clear All Filters Button */}
          {/* {(selectedCategories.length + selectedEmployees.length + selectedPeriods.length) > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedCategories([]);
                setSelectedEmployees([]);
                setSelectedPeriods([]);
              }}
              disabled={loading}
              className="text-gray-600 hover:text-gray-900"
            >
              <X className="h-4 w-4 mr-1" />
              Clear all filters
            </Button>
          )}
        </div>
      )} */}

      {/* Active Filters Display (Read-only pills when loading/after search) */}
      {loading && (selectedCategories.length + selectedEmployees.length + selectedPeriods.length) > 0 && (
        <div className="mb-4 flex flex-wrap gap-2 items-center">
          <span className="text-sm text-gray-600">Active filters:</span>
          {selectedCategories.map(cat => (
            <Badge key={`active-cat-${cat}`} variant="secondary" className="text-xs">
              <Building2 className="h-3 w-3 mr-1" />
              {cat}
            </Badge>
          ))}
          {selectedEmployees.map(emp => (
            <Badge key={`active-emp-${emp}`} variant="secondary" className="text-xs">
              <Users className="h-3 w-3 mr-1" />
              {emp}
            </Badge>
          ))}
          {selectedPeriods.map(period => (
            <Badge key={`active-period-${period}`} variant="secondary" className="text-xs">
              <Calendar className="h-3 w-3 mr-1" />
              {period}
            </Badge>
          ))}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="py-12 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-3" />
          <p className="text-gray-600">Searching pricing pages...</p>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="text-center py-12">
          <p className="text-gray-500">{error}</p>
        </div>
      )}

      {/* Results */}
      {!loading && response && response.total_results > 0 && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Results for "{response.query}"
              </h2>
            </div>
            <div className="flex gap-2">
              <Badge variant="secondary">
                {response.total_results} matches
              </Badge>
            </div>
          </div>

          {/* Selection controls */}
          {response.results.length > 0 && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const MIN_SCORE = 20;
                  const visibleResults = showAllResults
                    ? response.results
                    : response.results.filter(r => (r.score ?? 100) >= MIN_SCORE);
                  const visibleIndices = new Set(
                    visibleResults.map(r => response.results.indexOf(r))
                  );
                  setSelectedIndices(visibleIndices);
                  setIsSelectionMode(true);
                }}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Select all
              </button>
              {selectedIndices.size > 0 && (
                <>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={clearSelection}
                    className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                  >
                    Clear selection
                  </button>
                  <span className="text-xs text-gray-400">
                    ({selectedIndices.size} selected)
                  </span>
                </>
              )}
            </div>
          )}

          {/* Results grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {(() => {
              const MIN_SCORE = 20;
              const filteredResults = showAllResults
                ? response.results
                : response.results.filter(r => (r.score ?? 100) >= MIN_SCORE);

              return filteredResults.map((result, idx) => {
                // Get custom annotated URL if user has edited this result in lightbox
                const savedState = getSavedViewerState(result);
                // Find original index for selection tracking
                const originalIdx = response.results.indexOf(result);
                return (
                  <ResultTile
                    key={`${result.slug}-${result.version}-${originalIdx}`}
                    result={result}
                    index={originalIdx}
                    isSelected={selectedIndices.has(originalIdx)}
                    onSelect={() => toggleSelection(originalIdx)}
                    onClick={() => openLightbox(originalIdx)}
                    customAnnotatedUrl={savedState?.annotatedUrl}
                    searchQuery={response.query}
                  />
                );
              });
            })()}
          </div>

          {/* Show filtered count and "Show all" link */}
          {!showAllResults && (() => {
            const MIN_SCORE = 20;
            const filteredCount = response.results.filter(r => (r.score ?? 100) < MIN_SCORE).length;
            if (filteredCount > 0) {
              return (
                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600 mb-2">
                    {filteredCount} result{filteredCount > 1 ? 's' : ''} with low relevance hidden (score &lt; {MIN_SCORE})
                  </p>
                  <button
                    onClick={() => setShowAllResults(true)}
                    className="text-sm text-blue-600 hover:text-blue-800 underline font-medium"
                  >
                    Show all {response.results.length} results
                  </button>
                </div>
              );
            }
            return null;
          })()}

          {/* Floating action bar for selection */}
          {selectedIndices.size > 0 && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-gray-900 text-white px-4 py-3 rounded-full shadow-xl">
              <span className="text-sm font-medium">
                {selectedIndices.size} selected
              </span>
              <div className="w-px h-5 bg-gray-600" />
              <Button
                size="sm"
                variant="ghost"
                className="text-white hover:bg-gray-800 gap-2"
                onClick={handleAddSelectedToCollection}
              >
                <FolderPlus className="h-4 w-4" />
                Add to Collection
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-gray-400 hover:text-white hover:bg-gray-800"
                onClick={clearSelection}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && response && (
        <Lightbox
          results={response.results}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrev={goToPrev}
          onNext={goToNext}
          copiedField={copiedField}
          onCopy={handleCopy}
          onAddToCollection={handleAddSingleToCollection}
          getSavedViewerState={getSavedViewerState}
          onViewerStateChange={updateViewerState}
          searchQuery={response.query}
        />
      )}

      {/* Add to Collection Modal */}
      <AddToCollectionFromSearchModal
        open={showCollectionModal}
        onOpenChange={(open) => {
          setShowCollectionModal(open);
          if (!open) setSingleItemViewerState(null);
        }}
        items={itemsToAdd}
        viewerState={singleItemViewerState}
        searchQuery={query}
        onSuccess={() => {
          clearSelection();
          setSingleItemViewerState(null);
          setShowCollectionModal(false);
        }}
      />
    </div>
  );
};

interface ResultTileProps {
  result: SearchResultItem;
  index: number;
  isSelected?: boolean;
  onSelect?: () => void;
  onClick: () => void;
  customAnnotatedUrl?: string;
  searchQuery?: string;
}

const ResultTile: React.FC<ResultTileProps> = ({ result, isSelected, onSelect, onClick, customAnnotatedUrl, searchQuery }) => {
  // Track image loading state for fallback
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [imgError, setImgError] = useState(false);

  // Determine best image URL with fallback chain
  const primaryUrl = customAnnotatedUrl || result.cropped_url || result.image_url || result.cloudinary_url;
  const fallbackUrl = result.image_url || result.cloudinary_url;

  // Set initial image source
  useEffect(() => {
    setImgSrc(primaryUrl || null);
    setImgError(false);
  }, [primaryUrl]);

  const handleImageError = () => {
    // Try fallback URL if primary fails
    if (!imgError && fallbackUrl && imgSrc !== fallbackUrl) {
      setImgSrc(fallbackUrl);
      setImgError(true);
    }
  };

  const handleViewerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleSelectClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect?.();
  };

  return (
    <div
      className={cn(
        "cursor-pointer group rounded-lg overflow-hidden border bg-white transition-all hover:shadow-lg",
        isSelected ? "border-blue-500 ring-2 ring-blue-500/30" : "hover:border-blue-400"
      )}
    >
      {/* Image container */}
      <div className="relative" onClick={onClick}>
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={`${result.company_name} - ${result.matched_phrase || ''}`}
            className="w-full aspect-[4/3] object-cover"
            onError={handleImageError}
          />
        ) : (
          <div className="w-full aspect-[4/3] flex items-center justify-center bg-gray-100">
            <span className="text-2xl font-bold text-gray-300 uppercase">
              {result.slug.substring(0, 2)}
            </span>
          </div>
        )}

        {/* Selection checkbox - only element on image */}
        <button
          onClick={handleSelectClick}
          className={cn(
            "absolute top-2 left-2 w-5 h-5 rounded flex items-center justify-center transition-all shadow-sm border",
            isSelected
              ? "bg-blue-500 border-blue-500 text-white"
              : "bg-white/90 border-gray-300 text-gray-400 opacity-0 group-hover:opacity-100 hover:bg-white hover:border-gray-400"
          )}
        >
          {isSelected && <Check className="h-3.5 w-3.5" />}
        </button>
      </div>

      {/* Info below image */}
      <div className="p-2 space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <span className="text-sm font-medium text-gray-900 truncate">
              {result.company_name}
            </span>
            {result.score !== undefined && (
              <Badge variant="secondary" className="text-xs px-1 py-0 h-4 flex-shrink-0">
                {Math.round(result.score)}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {result.annotated_url && (
              <a
                href={result.annotated_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleViewerClick}
                className="flex-shrink-0 p-1 rounded hover:bg-gray-100 text-orange-500 hover:text-orange-600"
                title="Open annotated image"
              >
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                  <circle cx="18" cy="18" r="3" fill="currentColor" stroke="none" />
                </svg>
              </a>
            )}
            {result.image_url && (
              <a
                href={result.image_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleViewerClick}
                className="flex-shrink-0 p-1 rounded hover:bg-gray-100 text-blue-500 hover:text-blue-600"
                title="Open full image"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        </div>
        <p className="text-xs text-gray-500 truncate">
          {searchQuery
            ? highlightQueryTerms(result.matched_lines?.[0] || result.text_preview?.slice(0, 80) || '', searchQuery)
            : (result.matched_lines?.[0] || result.text_preview?.slice(0, 80))
          }
        </p>
      </div>
    </div>
  );
};

// Extended result item that includes viewer state for saving
interface SearchResultWithViewerState extends SearchResultItem {
  viewerState?: SearchImageViewerState;
}

interface LightboxProps {
  results: SearchResultItem[];
  currentIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  copiedField: string | null;
  onCopy: (text: string, field: string) => void;
  onAddToCollection?: (item: SearchResultItem, viewerState?: SearchImageViewerState) => void;
  getSavedViewerState: (item: SearchResultItem) => SearchImageViewerState | undefined;
  onViewerStateChange: (item: SearchResultItem, state: SearchImageViewerState) => void;
  searchQuery?: string;
}

const Lightbox: React.FC<LightboxProps> = ({
  results,
  currentIndex,
  onClose,
  onPrev,
  onNext,
  copiedField,
  onCopy,
  onAddToCollection,
  getSavedViewerState,
  onViewerStateChange,
  searchQuery,
}) => {
  const result = results[currentIndex];
  // Use original image_url for the viewer (fall back to cloudinary_url for legacy)
  const imageUrl = result.image_url || result.cloudinary_url;
  const copyKey = `lightbox-${result.slug}-${currentIndex}`;

  // Track current viewer state for saving
  const viewerStateRef = useRef<SearchImageViewerState | null>(null);

  // Parse marker position from annotated_url and convert to original image coordinates
  // URL format: c_crop,x_139,y_0,w_1800,h_1200/c_scale,w_1200,h_800/.../fl_layer_apply,g_north_west,x_369,y_195/...
  const parseMarkerFromAnnotatedUrl = (url: string | undefined): { x: number; y: number } | null => {
    if (!url) {
      return null;
    }

    try {
      // Parse crop parameters: c_crop,x_139,y_0,w_1800,h_1200
      const cropMatch = url.match(/c_crop,x_(\d+),y_(\d+),w_(\d+),h_(\d+)/);
      // Parse scale parameters: c_scale,w_1200,h_800
      const scaleMatch = url.match(/c_scale,w_(\d+),h_(\d+)/);
      // Parse marker position: fl_layer_apply,g_north_west,x_369,y_195
      const markerMatch = url.match(/fl_layer_apply,g_north_west,x_(\d+),y_(\d+)/);

      // Case 1: Explicit marker position (V2 exhaustive results with overlay)
      if (markerMatch) {
        const markerX = parseInt(markerMatch[1], 10);
        const markerY = parseInt(markerMatch[2], 10);

        // If we have crop and scale info, convert marker back to original image coordinates
        if (cropMatch && scaleMatch) {
          const cropX = parseInt(cropMatch[1], 10);
          const cropY = parseInt(cropMatch[2], 10);
          const cropW = parseInt(cropMatch[3], 10);
          const cropH = parseInt(cropMatch[4], 10);
          const scaleW = parseInt(scaleMatch[1], 10);
          const scaleH = parseInt(scaleMatch[2], 10);

          // Marker is in scaled coordinates, convert back to crop coordinates then to original
          const scaleFactorX = cropW / scaleW;
          const scaleFactorY = cropH / scaleH;
          const originalX = cropX + (markerX * scaleFactorX);
          const originalY = cropY + (markerY * scaleFactorY);

          return { x: Math.round(originalX), y: Math.round(originalY) };
        }

        // No crop/scale, use marker position directly
        return { x: markerX, y: markerY };
      }

      // Case 2: No explicit marker, but we have crop info (V3 results)
      // Use the center of the crop region as the approximate match location
      if (cropMatch) {
        const cropX = parseInt(cropMatch[1], 10);
        const cropY = parseInt(cropMatch[2], 10);
        const cropW = parseInt(cropMatch[3], 10);
        const cropH = parseInt(cropMatch[4], 10);

        // Center of crop in original image coordinates
        const centerX = cropX + Math.round(cropW / 2);
        const centerY = cropY + Math.round(cropH / 2);

        return { x: centerX, y: centerY };
      }

      // No marker and no crop info
      return null;
    } catch (err) {
      console.error('[Lightbox] Failed to parse annotated_url:', err);
      return null;
    }
  };

  // Get saved state or parse marker from annotated URL
  const savedState = getSavedViewerState(result);
  const parsedMarker = parseMarkerFromAnnotatedUrl(result.annotated_url);
  // Default to center of typical image if no marker found
  const defaultMarker = parsedMarker || { x: 500, y: 300 };

  // Use saved state if available, otherwise use defaults from URL
  const initialMarkerX = savedState?.marker.x ?? defaultMarker.x;
  const initialMarkerY = savedState?.marker.y ?? defaultMarker.y;

  // IMPORTANT: Only use marker position as viewport center if we actually have a parsed marker
  // For V3 results (no annotated_url), let viewport default to undefined so it shows sensible view
  const initialViewportCenterX = savedState?.cropCenter.x ?? (parsedMarker ? parsedMarker.x : undefined);
  const initialViewportCenterY = savedState?.cropCenter.y ?? (parsedMarker ? parsedMarker.y : undefined);

  const initialZoom = savedState?.viewport.zoom;
  const initialPanX = savedState?.viewport.panX;
  const initialPanY = savedState?.viewport.panY;

  const handleStateChange = useCallback((state: SearchImageViewerState) => {
    viewerStateRef.current = state;
    // Persist state to parent
    onViewerStateChange(result, state);
  }, [onViewerStateChange, result]);

  const handleAddToCollection = useCallback(() => {
    if (onAddToCollection) {
      onAddToCollection(result, viewerStateRef.current || undefined);
    }
  }, [onAddToCollection, result]);

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/50 flex-shrink-0">
        <div className="flex items-center gap-4">
          <h3 className="text-white font-semibold">{result.company_name}</h3>
          <span className="text-white/60 text-sm">
            {formatPeriod(result.period) ? `${formatPeriod(result.period)} · ` : ''}v{result.version}
          </span>
          <span className="text-white/40 text-sm">
            {currentIndex + 1} / {results.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {onAddToCollection && (
            <button
              onClick={handleAddToCollection}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors"
            >
              <FolderPlus className="h-4 w-4" />
              Save to Collection
            </button>
          )}
          {result.viewer_url && (
            <a
              href={result.viewer_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-white text-sm transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              Open in Viewer
            </a>
          )}
          <button
            onClick={onClose}
            className="p-2 rounded hover:bg-white/10 text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Main content - Image viewer */}
      <div className="flex-1 flex items-center justify-center relative min-h-0 overflow-hidden">
        {/* Prev button */}
        <button
          onClick={onPrev}
          className="absolute left-4 z-30 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>

        {/* Image Viewer with pan/zoom and draggable marker */}
        <div className="w-full h-full max-w-[calc(100%-8rem)] mx-16 overflow-hidden">
          {imageUrl ? (
            <SearchImageViewer
              key={`${result.slug}-${result.version}-${currentIndex}`}
              imageUrl={imageUrl}
              initialMarkerX={initialMarkerX}
              initialMarkerY={initialMarkerY}
              initialViewportCenterX={initialViewportCenterX}
              initialViewportCenterY={initialViewportCenterY}
              initialZoom={initialZoom}
              initialPanX={initialPanX}
              initialPanY={initialPanY}
              showMarker={!!parsedMarker}  // Only show marker if we have real coordinates
              onStateChange={handleStateChange}
              className="w-full h-full rounded-lg"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-800 rounded-lg">
              <span className="text-4xl font-bold text-gray-600 uppercase">
                {result.slug.substring(0, 2)}
              </span>
            </div>
          )}
        </div>

        {/* Next button */}
        <button
          onClick={onNext}
          className="absolute right-4 z-30 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-black/50 flex-shrink-0">
        <div className="text-sm text-white/80">
          {result.text_preview && (
            <span>
              {searchQuery
                ? highlightQueryTerms(result.text_preview, searchQuery, true)
                : result.text_preview}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Modal for adding search results to a collection
interface AddToCollectionFromSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: SearchResultItem[];
  viewerState?: SearchImageViewerState | null;
  searchQuery?: string;
  onSuccess: () => void;
}

const AddToCollectionFromSearchModal: React.FC<AddToCollectionFromSearchModalProps> = ({
  open,
  onOpenChange,
  items,
  viewerState,
  searchQuery = '',
  onSuccess,
}) => {
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [isAdding, setIsAdding] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [enableLLMCleanup, setEnableLLMCleanup] = useState(true);
  const [llmPrompt, setLLMPrompt] = useState('Extract details, stay factual. Write in clear, analytical tone. Keep under 280 chars long');
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [itemResults, setItemResults] = useState<Array<{
    slug: string;
    companyName: string;
    status: 'pending' | 'success' | 'error' | 'skipped';
    message?: string;
  }>>([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Handle explicit close action - resets state for next use
  const handleClose = () => {
    if (!isAdding) {
      // Reset all state for next time modal opens
      setProgress({ current: 0, total: 0 });
      setItemResults([]);
      onOpenChange(false);
    }
  };

  // Handle dialog's onOpenChange - don't allow closing during processing
  const handleDialogOpenChange = (newOpen: boolean) => {
    if (!newOpen && !isAdding) {
      // User is trying to close and we're not processing - allow it but reset state
      setProgress({ current: 0, total: 0 });
      setItemResults([]);
      onOpenChange(false);
    }
    // If isAdding is true, ignore the close attempt
  };

  // Fetch collections
  const { data: collectionsResponse, isLoading: collectionsLoading } = useQuery({
    queryKey: ['collections'],
    queryFn: getCollectionsQuery,
    staleTime: 5 * 60 * 1000,
  });
  const allCollections = collectionsResponse?.collections || [];

  // Filter out published collections - only show draft collections
  const collections = allCollections.filter(c =>
    c.canva_design_status !== 'published' && !c.published_at
  );

  // Get last used collection from localStorage
  const lastUsedCollectionId = typeof window !== 'undefined'
    ? localStorage.getItem('lastUsedCollection')
    : null;

  // Auto-select most recent collection and set subject when modal opens
  // Initialize itemResults only once when modal first opens (not when items change)
  useEffect(() => {
    if (open && collections.length > 0) {
      // Only auto-select if the last used collection is still unpublished
      const mostRecentCollection = lastUsedCollectionId && collections.find(c => c.id === lastUsedCollectionId)
        ? lastUsedCollectionId
        : collections[0].id;
      setSelectedCollectionId(mostRecentCollection);

      // Prepopulate subject with capitalized search query
      if (searchQuery) {
        setSubject(capitalizeWords(searchQuery));
      }

      // Initialize results for all items - ONLY run when modal opens, not when items change
      // This prevents resetting progress while items are being processed
      setItemResults(items.map(item => ({
        slug: item.slug,
        companyName: item.company_name,
        status: 'pending' as const,
      })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, collections.length, lastUsedCollectionId, searchQuery]);

  const handleAdd = async () => {
    if (!selectedCollectionId || items.length === 0) return;

    // Validate LLM cleanup settings
    if (enableLLMCleanup && !llmPrompt.trim()) {
      toast({
        title: "Prompt Required",
        description: "Please provide a cleanup prompt when AI cleanup is enabled.",
        variant: "destructive",
      });
      return;
    }

    setIsAdding(true);
    setProgress({ current: 0, total: items.length });

    // Save last used collection
    if (typeof window !== 'undefined') {
      localStorage.setItem('lastUsedCollection', selectedCollectionId);
    }

    let successCount = 0;
    let errorCount = 0;
    let skippedCount = 0;
    let processedCount = 0;

    // Import supabase for page_id lookup
    const { supabase } = await import('@/integrations/supabase/client');

    // Helper function to process a single item
    const processItem = async (item: typeof items[0], index: number) => {
      try {
        // Look up the page_id from pages_view using the full slug
        const { data: pageData, error: pageError } = await supabase
          .from('pages_view')
          .select('page_id')
          .eq('slug', item.slug)
          .single();

        if (pageError || !pageData?.page_id) {
          console.error(`Could not find page_id for slug: ${item.slug}`, pageError);
          setItemResults(prev => prev.map((r, idx) =>
            idx === index ? { ...r, status: 'error' as const, message: `Page not found: ${pageError?.message || 'Unknown error'}` } : r
          ));
          return { status: 'error' as const };
        }

        // Check for duplicates if skipDuplicates is enabled
        if (skipDuplicates) {
          const { data: existingAssets, error: duplicateError } = await supabase
            .from('app_assets')
            .select('id')
            .eq('collection_id', selectedCollectionId)
            .eq('slug', item.slug)
            .limit(1);

          if (duplicateError) {
            console.error(`Error checking for duplicates: ${item.slug}`, duplicateError);
            setItemResults(prev => prev.map((r, idx) =>
              idx === index ? { ...r, status: 'error' as const, message: `Duplicate check failed: ${duplicateError.message}` } : r
            ));
            return { status: 'error' as const };
          }

          if (existingAssets && existingAssets.length > 0) {
            console.log(`Skipping duplicate: ${item.slug}`);
            setItemResults(prev => prev.map((r, idx) =>
              idx === index ? { ...r, status: 'skipped' as const, message: 'Already exists in collection' } : r
            ));
            return { status: 'skipped' as const };
          }
        }

        // Use viewerState for single item if available
        const hasViewerState = items.length === 1 && viewerState;

        // Use cropped+annotated URL from viewer state if available (has user's viewport crop with marker)
        // Otherwise fall back to the original annotated_url from search results
        const croppedUrl = hasViewerState && viewerState.croppedAndAnnotatedUrl
          ? viewerState.croppedAndAnnotatedUrl
          : item.annotated_url;

        // Extract marker position from the URL if not from viewer state
        let markerX: number | undefined;
        let markerY: number | undefined;

        if (hasViewerState) {
          // Use viewer state marker
          markerX = Math.round(viewerState.marker.x);
          markerY = Math.round(viewerState.marker.y);
        } else if (croppedUrl) {
          // Extract marker from URL
          const marker = extractMarkerFromUrl(croppedUrl);
          if (marker) {
            markerX = marker.x;
            markerY = marker.y;
          }
        }

        // Process subheader with LLM cleanup if enabled
        let subheader = item.text_preview || undefined;

        if (enableLLMCleanup && llmPrompt && item.text_preview) {
          try {
            const cleaned = await cleanHighlightWithLLM(item.text_preview, llmPrompt);
            subheader = cleaned;
          } catch (error) {
            console.error('LLM cleanup failed for item, using raw highlight:', error);
            // Fall back to raw highlight - subheader already set above
          }
        }

        // Create the asset with the proper page_id UUID
        await createAssetDirectDB({
          collection_id: selectedCollectionId,
          type: 'simple',
          comparison_mode: 'single',
          page_id: pageData.page_id,
          slug: item.slug,
          version: item.version,
          title: `${item.company_name} - ${item.matched_phrase || item.text_preview?.slice(0, 50) || 'Search result'}`,
          description: item.text_preview || `Search result`,
          header: subject || undefined,
          subheader: subheader,
          primary_original_url: item.image_url || item.cloudinary_url,
          primary_cropped_url: croppedUrl,
          // Set marker coordinates from viewer state or extracted from URL
          primary_marker_x: markerX,
          primary_marker_y: markerY,
          // Store viewport state for the viewer
          crop_coordinates: hasViewerState ? {
            zoom: viewerState.viewport.zoom,
            panX: viewerState.viewport.panX,
            panY: viewerState.viewport.panY,
            markerX: viewerState.marker.x,
            markerY: viewerState.marker.y,
          } : undefined,
        });
        setItemResults(prev => prev.map((r, idx) =>
          idx === index ? { ...r, status: 'success' as const, message: 'Added successfully' } : r
        ));
        return { status: 'success' as const };
      } catch (error) {
        console.error(`Failed to add ${item.company_name}:`, error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        setItemResults(prev => prev.map((r, idx) =>
          idx === index ? { ...r, status: 'error' as const, message: errorMessage } : r
        ));
        return { status: 'error' as const };
      }
    };

    // Process items in batches of 10
    const BATCH_SIZE = 10;
    for (let i = 0; i < items.length; i += BATCH_SIZE) {
      const batch = items.slice(i, Math.min(i + BATCH_SIZE, items.length));
      const batchPromises = batch.map((item, batchIndex) =>
        processItem(item, i + batchIndex).then(result => {
          processedCount++;
          setProgress({ current: processedCount, total: items.length });
          return result;
        })
      );

      // Wait for all items in the batch to complete
      const results = await Promise.all(batchPromises);

      // Count results
      results.forEach(result => {
        if (result.status === 'success') successCount++;
        else if (result.status === 'error') errorCount++;
        else if (result.status === 'skipped') skippedCount++;
      });
    }

    setIsAdding(false);

    // Invalidate queries to refresh data
    queryClient.invalidateQueries({ queryKey: ['collections'] });
    queryClient.invalidateQueries({ queryKey: ['assets'] });

    // Show toast notification with link to collection
    const collection = collections.find(c => c.id === selectedCollectionId);

    if (successCount > 0) {
      toast({
        title: "Added to Collection",
        description: (
          <div className="flex flex-col gap-1">
            <p>
              {successCount} item{successCount > 1 ? 's' : ''} added to {collection?.name || 'collection'}
              {skippedCount > 0 && ` (${skippedCount} skipped)`}
              {errorCount > 0 && ` (${errorCount} failed)`}
            </p>
            <a
              href={`/collections/${selectedCollectionId}`}
              className="text-blue-600 hover:text-blue-800 underline text-sm"
            >
              View collection →
            </a>
          </div>
        ),
      });
    } else if (errorCount > 0 && skippedCount === 0) {
      toast({
        title: "Failed to add items",
        description: `All ${errorCount} item${errorCount > 1 ? 's' : ''} failed. Check the details above.`,
        variant: "destructive",
      });
    } else if (skippedCount > 0 && successCount === 0 && errorCount === 0) {
      toast({
        title: "All items skipped",
        description: `${skippedCount} item${skippedCount > 1 ? 's were' : ' was'} already in the collection.`,
      });
    }
  };

  if (collectionsLoading) {
    return (
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="max-w-md">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Loading collections...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!collections || collections.length === 0) {
    const allPublished = allCollections.length > 0;
    return (
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>No Unpublished Collections Available</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600 mb-4">
              {allPublished
                ? `You have ${allCollections.length} collection${allCollections.length > 1 ? 's' : ''}, but ${allCollections.length > 1 ? 'they are' : 'it is'} already published. Published collections cannot be modified. Please create a new collection to add assets.`
                : 'You need to create a collection first before adding assets.'}
            </p>
            <div className="flex justify-end">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Add {items.length} item{items.length > 1 ? 's' : ''} to Collection
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Preview of items being added */}
          {items.length > 0 && (
            <div className="text-sm text-gray-500">
              {items.length === 1 ? (
                <span>{items[0].company_name} - {items[0].matched_phrase}</span>
              ) : (
                <span>
                  {items.slice(0, 3).map(i => i.company_name).join(', ')}
                  {items.length > 3 && ` and ${items.length - 3} more`}
                </span>
              )}
            </div>
          )}

          {/* Subject field */}
          <div className="space-y-2">
            <Label htmlFor="subject-input">
              Subject
            </Label>
            <Input
              id="subject-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject (e.g., Black Friday, Cyber Monday)"
              disabled={isAdding}
              className="w-full"
            />
          </div>

          {/* LLM Cleanup Toggle */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="llm-cleanup"
                  checked={enableLLMCleanup}
                  onCheckedChange={(checked) => setEnableLLMCleanup(checked as boolean)}
                  disabled={isAdding}
                />
                <Label htmlFor="llm-cleanup" className="text-sm font-normal cursor-pointer">
                  Clean up highlights with AI
                </Label>
              </div>
              {enableLLMCleanup && (
                <button
                  type="button"
                  onClick={() => setShowPromptEditor(!showPromptEditor)}
                  className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                  disabled={isAdding}
                >
                  {showPromptEditor ? (
                    <>
                      <ChevronUp className="h-3 w-3" />
                      Hide prompt
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-3 w-3" />
                      Customize prompt
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Prompt Input (conditional and collapsible) */}
            {enableLLMCleanup && showPromptEditor && (
              <div className="space-y-2 pl-6">
                <Label htmlFor="llm-prompt" className="text-xs">Custom Prompt</Label>
                <Textarea
                  id="llm-prompt"
                  value={llmPrompt}
                  onChange={(e) => setLLMPrompt(e.target.value)}
                  placeholder="E.g., 'Extract discount amount and details within 240 characters'"
                  rows={3}
                  disabled={isAdding}
                  className="text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  This prompt will be used to clean up each highlight before saving
                </p>
              </div>
            )}
          </div>

          {/* Skip Duplicates Toggle */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="skip-duplicates"
              checked={skipDuplicates}
              onCheckedChange={(checked) => setSkipDuplicates(checked as boolean)}
              disabled={isAdding}
            />
            <Label htmlFor="skip-duplicates" className="text-sm font-normal cursor-pointer">
              Skip duplicates (don't add items already in collection)
            </Label>
          </div>

          {/* Collection selector */}
          <div className="space-y-2">
            <Label htmlFor="collection-select">
              Select Collection
            </Label>
            <Select
              value={selectedCollectionId}
              onValueChange={setSelectedCollectionId}
              disabled={isAdding}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose a collection" />
              </SelectTrigger>
              <SelectContent>
                {collections.map((collection) => (
                  <SelectItem key={collection.id} value={collection.id}>
                    <div className="flex items-center justify-between w-full">
                      <span>{collection.name}</span>
                      {collection.id === lastUsedCollectionId && (
                        <span className="text-xs text-gray-500 ml-2">(Recent)</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Progress indicator */}
          {isAdding && (
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Processing {progress.current} of {progress.total}...</span>
                </div>
                <span className="text-xs text-gray-500">
                  {itemResults.filter(r => r.status === 'success').length} added,{' '}
                  {itemResults.filter(r => r.status === 'error').length} failed,{' '}
                  {itemResults.filter(r => r.status === 'skipped').length} skipped
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>

              {/* Scrollable results list */}
              <div className="max-h-64 overflow-y-auto space-y-1 border rounded-md p-2 bg-gray-50">
                {itemResults.map((result, idx) => (
                  <div
                    key={`${result.slug}-${idx}`}
                    className={cn(
                      "flex items-start gap-2 text-xs p-2 rounded",
                      result.status === 'success' && "bg-green-50 text-green-800",
                      result.status === 'error' && "bg-red-50 text-red-800",
                      result.status === 'skipped' && "bg-yellow-50 text-yellow-800",
                      result.status === 'pending' && "bg-gray-100 text-gray-500"
                    )}
                  >
                    {result.status === 'success' && <Check className="h-3 w-3 mt-0.5 flex-shrink-0" />}
                    {result.status === 'error' && <X className="h-3 w-3 mt-0.5 flex-shrink-0" />}
                    {result.status === 'skipped' && <span className="text-[10px] mt-0.5 flex-shrink-0">⊘</span>}
                    {result.status === 'pending' && <Loader2 className="h-3 w-3 mt-0.5 flex-shrink-0 animate-spin" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{result.companyName}</div>
                      {result.message && (
                        <div className="text-[10px] opacity-80 mt-0.5">{result.message}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Completion Summary */}
          {!isAdding && progress.current > 0 && (
            <div className="border-t pt-4 space-y-3">
              <div className="text-sm font-medium">Results Summary</div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="bg-green-50 border border-green-200 rounded p-3 text-center">
                  <div className="text-2xl font-bold text-green-700">
                    {itemResults.filter(r => r.status === 'success').length}
                  </div>
                  <div className="text-xs text-green-600 mt-1">Added</div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-center">
                  <div className="text-2xl font-bold text-yellow-700">
                    {itemResults.filter(r => r.status === 'skipped').length}
                  </div>
                  <div className="text-xs text-yellow-600 mt-1">Skipped</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded p-3 text-center">
                  <div className="text-2xl font-bold text-red-700">
                    {itemResults.filter(r => r.status === 'error').length}
                  </div>
                  <div className="text-xs text-red-600 mt-1">Failed</div>
                </div>
              </div>

              {/* Show detailed error list if there are errors */}
              {itemResults.filter(r => r.status === 'error').length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-medium text-red-700">Failed Items:</div>
                  <div className="max-h-32 overflow-y-auto space-y-1 text-xs">
                    {itemResults
                      .filter(r => r.status === 'error')
                      .map((result, idx) => (
                        <div key={idx} className="bg-red-50 border border-red-200 rounded p-2">
                          <div className="font-medium text-red-800">{result.companyName}</div>
                          <div className="text-red-600 text-[10px] mt-0.5">{result.message}</div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isAdding}
            >
              {!isAdding && progress.current > 0 ? 'Close' : 'Cancel'}
            </Button>
            {!isAdding && progress.current > 0 && itemResults.filter(r => r.status === 'success').length > 0 && (
              <Button
                onClick={() => {
                  window.location.href = `/collections/${selectedCollectionId}`;
                }}
                className="flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                View Collection
              </Button>
            )}
            {(!isAdding && progress.current === 0) && (
              <Button
                onClick={handleAdd}
                disabled={!selectedCollectionId}
                className="flex items-center gap-2"
              >
                <FolderPlus className="h-4 w-4" />
                Add {items.length > 1 ? `${items.length} Items` : 'Item'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Search;
